import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { getApiErrorMessage } from '@/lib/utils';
import { paymentsApi, paymentsQueryKeys, paymentMethodsApi, paymentMethodsQueryKeys, billsQueryKeys } from '../api';
import type { PaymentFilters, CreatePaymentDto, UpdatePaymentDto, AttachBillDto } from '../types';

// Hook for fetching payments list
export function usePaymentsList(filters?: PaymentFilters) {
  return useQuery({
    queryKey: paymentsQueryKeys.list(filters),
    queryFn: () => paymentsApi.getAll(filters),
    staleTime: 5 * 60 * 1000,
  });
}

// Hook for fetching single payment
export function usePayment(id: number | string | undefined) {
  return useQuery({
    queryKey: paymentsQueryKeys.detail(id ?? ''),
    queryFn: () => paymentsApi.getById(id!),
    enabled: !!id,
    retry: 1,
    staleTime: 5 * 60 * 1000,
  });
}

// Hook for payment mutations
export function usePaymentMutations() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // Recording, re-amounting or removing a payment settles the linked bill and
  // moves the contract balance server-side, so every one of these invalidates
  // bills as well as payments.
  const createMutation = useMutation({
    mutationFn: (data: CreatePaymentDto) => paymentsApi.create(data),
    onSuccess: () => {
      toast.success(t('paymentCreatedSuccess'));
      void queryClient.invalidateQueries({ queryKey: paymentsQueryKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: billsQueryKeys.all });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('errorCreatingPayment')));
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number | string; data: UpdatePaymentDto }) =>
      paymentsApi.update({ id, data }),
    onSuccess: (_, { id }) => {
      toast.success(t('paymentUpdatedSuccess'));
      void queryClient.invalidateQueries({ queryKey: paymentsQueryKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: paymentsQueryKeys.detail(id) });
      void queryClient.invalidateQueries({ queryKey: billsQueryKeys.all });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('errorUpdatingPayment')));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number | string) => paymentsApi.delete(id),
    onSuccess: () => {
      toast.success(t('paymentDeletedSuccess'));
      void queryClient.invalidateQueries({ queryKey: paymentsQueryKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: billsQueryKeys.all });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('errorDeletingPayment')));
    },
  });

  // Allocate an unallocated (advance) payment to a bill
  const attachBillMutation = useMutation({
    mutationFn: ({ id, data }: { id: number | string; data: AttachBillDto }) =>
      paymentsApi.attachBill({ id, data }),
    onSuccess: (_, { id }) => {
      toast.success(t('paymentAttachedSuccess'));
      void queryClient.invalidateQueries({ queryKey: paymentsQueryKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: paymentsQueryKeys.detail(id) });
      // The bill's paid/state can change on allocation — refresh bills too.
      void queryClient.invalidateQueries({ queryKey: billsQueryKeys.all });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('errorAttachingPayment')));
    },
  });

  return {
    createMutation,
    updateMutation,
    deleteMutation,
    attachBillMutation,
  };
}

// Hook for fetching payment methods
export function usePaymentMethods() {
  return useQuery({
    queryKey: paymentMethodsQueryKeys.list(),
    queryFn: () => paymentMethodsApi.getAll(),
    staleTime: 30 * 60 * 1000, // 30 minutes
  });
}
